'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { warehouseApi, storageLocationApi, Warehouse } from '@parts/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import {
  Warehouse as WarehouseIcon,
  Plus,
  Search,
  Edit2,
  Trash2,
  MapPin,
  Package,
  ChevronRight,
  Star,
  MoreVertical,
} from 'lucide-react';

export default function WarehousesPage() {
  const router = useRouter();
  const { t, locale } = useTranslation();
  const confirm = useConfirm();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    nameEn: '',
    nameCn: '',
    description: '',
    address: '',
    contactPerson: '',
    contactPhone: '',
    isDefault: false,
  });

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehouseApi.list({ limit: 1000 });
      setWarehouses(response.items || []);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
      toast.error(t.warehouse.createFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.code || !formData.nameEn) {
      toast.error(locale === 'zh' ? '请填写必填字段' : 'Please fill in required fields');
      return;
    }

    try {
      await warehouseApi.create(formData);
      toast.success(t.warehouse.createSuccess);
      setShowCreateModal(false);
      resetForm();
      loadWarehouses();
    } catch (error: any) {
      console.error('Failed to create warehouse:', error);
      toast.error(error.response?.data?.message || t.warehouse.createFailed);
    }
  };

  const handleUpdate = async () => {
    if (!editingWarehouse) return;

    try {
      await warehouseApi.update(editingWarehouse.id, formData);
      toast.success(t.warehouse.updateSuccess);
      setEditingWarehouse(null);
      resetForm();
      loadWarehouses();
    } catch (error: any) {
      console.error('Failed to update warehouse:', error);
      toast.error(error.response?.data?.message || t.warehouse.updateFailed);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t.warehouse.deleteWarehouseConfirm,
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await warehouseApi.delete(id);
      toast.success(t.warehouse.deleteSuccess);
      loadWarehouses();
    } catch (error: any) {
      console.error('Failed to delete warehouse:', error);
      toast.error(error.response?.data?.message || t.warehouse.deleteFailed);
    }
  };

  const openEditModal = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      code: warehouse.code,
      nameEn: warehouse.nameEn,
      nameCn: warehouse.nameCn || '',
      description: warehouse.description || '',
      address: warehouse.address || '',
      contactPerson: warehouse.contactPerson || '',
      contactPhone: warehouse.contactPhone || '',
      isDefault: warehouse.isDefault,
    });
  };

  const resetForm = () => {
    setFormData({
      code: '',
      nameEn: '',
      nameCn: '',
      description: '',
      address: '',
      contactPerson: '',
      contactPhone: '',
      isDefault: false,
    });
  };

  const filteredWarehouses = warehouses.filter(
    (w) =>
      w.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.nameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (w.nameCn && w.nameCn.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      MAINTENANCE: 'bg-yellow-100 text-yellow-800',
    };
    const labels: Record<string, string> = {
      ACTIVE: t.warehouse.statusActive,
      INACTIVE: t.warehouse.statusInactive,
      MAINTENANCE: t.warehouse.statusMaintenance,
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.ACTIVE}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t.warehouse.title}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {locale === 'zh' ? '管理仓库和仓位信息' : 'Manage warehouses and storage locations'}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t.warehouse.createWarehouse}
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder={t.warehouse.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Warehouse List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">{t.common.loading}</p>
        </div>
      ) : filteredWarehouses.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <WarehouseIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">{t.warehouse.noWarehouse}</p>
          <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t.warehouse.createWarehouse}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWarehouses.map((warehouse) => (
            <div
              key={warehouse.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <WarehouseIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-gray-900">{warehouse.code}</h3>
                        {warehouse.isDefault && (
                          <Star className="w-4 h-4 text-yellow-500 ml-2" fill="currentColor" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {locale === 'zh' ? (warehouse.nameCn || warehouse.nameEn) : warehouse.nameEn}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(warehouse.status)}
                </div>

                {warehouse.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">{warehouse.description}</p>
                )}

                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {warehouse._count?.storageLocations || 0} {t.warehouse.locationCount}
                    </span>
                    <span className="flex items-center">
                      <Package className="w-4 h-4 mr-1" />
                      {warehouse._count?.parts || 0} {t.warehouse.partsCount}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => router.push(`/parts/warehouses/${warehouse.id}/locations`)}
                  >
                    {t.warehouse.viewLocations}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(warehouse)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(warehouse.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingWarehouse) && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingWarehouse ? t.warehouse.editWarehouse : t.warehouse.createWarehouse}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.warehouse.code} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="WH-001"
                    disabled={!!editingWarehouse}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.warehouse.nameEn} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.nameEn}
                    onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                    placeholder="Main Warehouse"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.warehouse.nameCn}
                </label>
                <Input
                  value={formData.nameCn}
                  onChange={(e) => setFormData({ ...formData, nameCn: e.target.value })}
                  placeholder="主仓库"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.warehouse.description}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.warehouse.address}
                </label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.warehouse.contactPerson}
                  </label>
                  <Input
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.warehouse.contactPhone}
                  </label>
                  <Input
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                  {t.warehouse.isDefault}
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingWarehouse(null);
                  resetForm();
                }}
              >
                {t.common.cancel}
              </Button>
              <Button onClick={editingWarehouse ? handleUpdate : handleCreate}>
                {editingWarehouse ? t.common.save : t.common.create}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
